import { BffResponseObject } from 'sources/model/response';
import { PzOverallPerformance, PzPerformanceProgramBreakdown } from './../models/pz';
import { MetricBrandDetails, MetricsDetails } from 'src/sources/model/pz-response';
import { SerializedData, Store } from 'bernie-core';
import { Logger } from 'bernie-logger';
import { PartnerPerfSource } from 'sources/partner-perf-source';
import { makeObservable, observable, flow, computed } from 'mobx';
import { PzAnalytics, Program } from 'models';
import * as _groupBy from 'lodash.groupby';
import { Progress } from 'src/models/common';

export class PartnerPerfStore extends Store {
  public analytics: MetricsDetails[];
  public analyticsState: Progress = 'pending';

  private readonly partnerPerfSource: PartnerPerfSource;

  public constructor(partnerPerfSource: PartnerPerfSource, state: SerializedData = {}, logger: Logger) {
    super(state, logger);
    this.partnerPerfSource = partnerPerfSource;

    makeObservable(this, {
      analytics: observable,
      analyticsState: observable,
      fetchPzAnalytics: flow,
      overallPerformance: computed,
    });
    this.analytics = [];
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  public *fetchPzAnalytics(metricRequest: PzAnalytics) {
    this.analyticsState = 'pending';
    try {
      const newPzAnalytics: BffResponseObject<MetricsDetails[]> = yield this.partnerPerfSource.getPzAnalytics(
        metricRequest,
      );
      this.analytics = newPzAnalytics.resBody;
      this.analyticsState = 'done';
    } catch (error) {
      this.analyticsState = 'error';
    }
  }

  /**
   * get the overall performance for all programs that user has joined
   */
  get overallPerformance(): PzOverallPerformance {
    const performance: PzOverallPerformance = {
      totalClicks: 0,
      totalConversions: 0,
      totalOrderValue: 0,
      totalPartnerCommission: 0,
    };
    this.analytics.forEach((metric) => {
      performance.totalClicks += metric.total_clicks ?? 0;
      performance.totalConversions += metric.total_conversions ?? 0;
      performance.totalOrderValue += metric.total_order_value ?? 0;
      performance.totalPartnerCommission += metric.total_partner_commission ?? 0;
    });

    return performance;
  }

  /**
   * get the program metrics breakdown that is grouped by brand
   * @param programs programs from program store
   * @returns program metrics breakdown used for table component
   */
  public getProgramMetricsBreakdown(programs: Program[]): PzPerformanceProgramBreakdown[] {
    // adding title and brand info to each metric detail from programs
    const programMetrics: MetricBrandDetails[] = this.analytics.map((datum) => {
      const pwaProgram = programs.find((program) => program.name === datum.campaign_name);

      return {
        ...datum,
        title: pwaProgram.title,
        brand: pwaProgram.brand,
      };
    });

    //group metrics by brand
    const metricsGroupedByBrand: { [key: string]: MetricBrandDetails[] } = _groupBy(programMetrics, 'brand');

    //modify data to suit program breakdown data structure
    const allBrands = Object.keys(metricsGroupedByBrand);
    const programMetricsBreakDown: PzPerformanceProgramBreakdown[] = allBrands.map((brand) => {
      const performance: PzOverallPerformance = {
        totalClicks: 0,
        totalConversions: 0,
        totalOrderValue: 0,
        totalPartnerCommission: 0,
      };
      const programsBrand = metricsGroupedByBrand[brand];
      programsBrand.forEach((datum) => {
        performance.totalClicks += datum.total_clicks ?? 0;
        performance.totalConversions += datum.total_conversions ?? 0;
        performance.totalOrderValue += datum.total_order_value ?? 0;
        performance.totalPartnerCommission += datum.total_partner_commission ?? 0;
      });

      return {
        brandTitle: brand,
        programs: programsBrand,
        totalClicks: performance.totalClicks,
        totalConversions: performance.totalConversions,
        totalOrderValue: performance.totalOrderValue,
        totalPartnerCommission: performance.totalPartnerCommission,
      };
    });

    return programMetricsBreakDown;
  }
}
